import { useRef } from "react";
import { useBlocker } from "react-router-dom";

import { useBeforeUnloadGuard } from "./useBeforeUnloadGuard";

/**
 * Blocks in-app navigation and tab close while a workspace requires exit
 * confirmation.
 *
 * The router blocker and `beforeunload` cover different exits and neither
 * subsumes the other: `useBlocker` cannot see a tab close, and `beforeunload`
 * cannot see a client-side route change. Both are needed, and both are keyed on
 * the same flag unless the caller separates them with `guardUnload`. Search-only navigation is opt-in
 * because workspace tabs can use the query string without leaving the editor.
 */
export const useUnsavedWorkspaceGuard = (
  shouldBlock: boolean,
  {
    includeSearchChanges = false,
    guardUnload = shouldBlock,
  }: {
    includeSearchChanges?: boolean;
    /**
     * Whether a tab close is guarded too. Defaults to `shouldBlock`; a surface
     * whose work is already persisted can intercept in-app exits (to ask a
     * question) without warning about a tab close that loses nothing.
     */
    guardUnload?: boolean;
  } = {}
) => {
  const allowNextNavigationRef = useRef(false);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (allowNextNavigationRef.current) {
      allowNextNavigationRef.current = false;
      return false;
    }

    return (
      shouldBlock &&
      (currentLocation.pathname !== nextLocation.pathname ||
        (includeSearchChanges &&
          currentLocation.search !== nextLocation.search))
    );
  });

  useBeforeUnloadGuard(guardUnload);

  return {
    isBlocked: blocker.state === "blocked",
    /** Leave anyway. Safe to call when nothing is blocked. */
    proceed: () => blocker.proceed?.(),
    /** Stay put. Safe to call when nothing is blocked. */
    reset: () => blocker.reset?.(),
    /** Let a completed create flow navigate to the rule that it produced. */
    allowNextNavigation: () => {
      allowNextNavigationRef.current = true;
    },
  };
};
